<script setup>
import {onBeforeUnmount, onMounted, ref, watch} from 'vue'
import {useProductStore} from '@js/store/ProductStore.js'
import MobileTopBar from "@js/components/TopBar/MobileTopBar.vue"
import Breadcrumbs from "@js/components/Breadcrumbs/Breadcrumbs.vue"
import ProductTitle from "@js/components/Sections/Product/ProductTitle.vue"
import IconImage from "@js/components/Icons/IconImage.vue"
import ProductCartForm from "@js/components/Sections/Product/ProductCartForm.vue"
import BaseImageSlider from "@js/components/Images/BaseImageSlider.vue"
import ProductMainData from "@js/components/Sections/Product/ProductMainData.vue"
import TabsComponent from "@js/components/Tabs/TabsComponent.vue"
import ProductAllCharacteristics from "@js/components/Sections/Product/Tabs/ProductAllCharacteristics.vue"
import ProductFaq from "@js/components/Sections/Product/Tabs/ProductFaq.vue"
import ProductCollectionProductSlider from "@js/components/Sections/Product/Tabs/ProductCollectionProductSlider.vue"
import OnlyDesktop from "@js/components/DynamicComponents/OnlyDesktop.vue";
import OnlyMobile from "@js/components/DynamicComponents/OnlyMobile.vue";
import ThreeDView from "@js/components/Sections/3DView/ThreeDView.vue";
import {use3DViewStore} from "@js/store/3dViewStore.js";
import IconARBox from "@js/components/Icons/IconARBox.vue";

const props = defineProps({
    product: {
        type: Object,
        required: true,
    },
})

const productStore = useProductStore()
productStore.product = props.product

const threeDViewStore = use3DViewStore()

const currentTab = ref(1)

const tabs = [
    {id: 1, title: 'Все характеристики'},
    {id: 2, title: 'Описание'},
    {id: 3, title: 'Частые вопросы'},
]

function stripHtml(html) {
    const tmp = document.createElement("div")
    tmp.innerHTML = html
    let text = tmp.textContent || tmp.innerText || ""
    return text.replace(/\n/g, " ").replace(/\s+/g, " ").trim()
}

function insertSchemaMarkup() {
    if (typeof window !== "undefined") {
        const scriptId = "schema-product-jsonld"
        let script = document.getElementById(scriptId)
        if (script) {
            script.remove()
        }

        script = document.createElement("script")
        script.id = scriptId
        script.type = "application/ld+json"
        script.textContent = JSON.stringify({
            "@context": "https://schema.org/",
            "@type": "Product",
            "name": productStore.product?.title ?? "Неизвестный продукт",
            "image": productStore.images?.[0] ?? "/assets/images/defaults/product-not-found.webp",
            "description": productStore.product?.media?.description
                ? stripHtml(productStore.product.media.description)
                : "Описание отсутствует",
            "sku": productStore.product?.article ?? "Не указан",
            "brand": {
                "@type": "Brand",
                "name": "ООО «ModaCeramics»"
            },
            "offers": {
                "@type": "Offer",
                "price": productStore.product?.price ?? "0",
                "priceCurrency": "RUB",
                "availability": productStore.product?.availability
                    ? "https://schema.org/InStock"
                    : "https://schema.org/OutOfStock",
                "url": window.location.href
            }
        })

        document.head.appendChild(script)
    }
}

function removeSchemaMarkup() {
    const script = document.getElementById("schema-product-jsonld")
    if (script) {
        script.remove()
    }
}

onMounted(() => {
    productStore.loadImages()
})

onBeforeUnmount(() => {
    removeSchemaMarkup()
})

watch(
    () => productStore.images,
    (val) => {
        if (val !== undefined) {
            insertSchemaMarkup()
        }
    },
    {deep: true}
)
</script>

<template>
    <MobileTopBar/>
    <div class="container">
        <Breadcrumbs/>

        <OnlyMobile :maxSize="1024">
            <ProductTitle/>
        </OnlyMobile>

        <div class="grid grid-cols-12 gap-y-5 md:gap-y-10 md:gap-x-10 text-gray-900 mb-10">
            <div class="col-span-12 md:col-span-7 lg:col-span-5 order-1 relative">
                <template v-if="!productStore.loadings.imagesLoading && productStore.images">
                    <BaseImageSlider :images="productStore.images"/>
                </template>
                <template v-else>
                    <div role="status" class="space-y-4 animate-pulse">
                        <div class="flex items-center justify-center h-56 md:h-96 bg-gray-300 rounded-2xl">
                            <IconImage class="w-24 h-24"/>
                        </div>
                    </div>
                </template>
                <div v-if="product.interior_link" @click.prevent="threeDViewStore.showPopup(product.interior_link)"
                     class="bg-baseColor md:hover:bg-black text-black md:hover:text-white absolute top-0 right-0 p-3 z-10 rounded-tr-2xl rounded-bl-2xl transition cursor-pointer">
                    <IconARBox class="w-8 h-8 transition"/>
                </div>
            </div>

            <div class="col-span-12 lg:col-span-4 order-3 lg:order-2">
                <OnlyDesktop :maxSize="1024" class="mb-5">
                    <ProductTitle/>
                </OnlyDesktop>
                <ProductMainData/>
            </div>

            <div class="col-span-12 md:col-span-5 lg:col-span-3 order-2 lg:order-3">
                <ProductCartForm :key="productStore.id"/>
            </div>
        </div>

        <div class="shadow-lg rounded-2xl mb-10">
            <TabsComponent v-model="currentTab" :tabs="tabs"/>
            <div class="border border-t-0 rounded-b-2xl p-5">
                <div v-show="currentTab === 1">
                    <ProductAllCharacteristics/>
                </div>
                <div v-show="currentTab === 2">
                    <div v-if="productStore.product.media.description"
                         class="text-sm font-medium flex flex-col gap-1"
                         v-html="productStore.product.media.description"></div>
                    <div v-else class="font-medium">Описания пока нет</div>
                </div>
                <div v-show="currentTab === 3">
                    <ProductFaq/>
                </div>
            </div>
        </div>
    </div>

    <div class="max-md:ps-5 sm:container">
        <ProductCollectionProductSlider/>
    </div>
    <ThreeDView v-if="product.interior_link"/>
</template>

<style scoped lang="scss"></style>
